import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, TrendingUp, TrendingDown, Minus, ShieldAlert, AlertTriangle,
  Target, ChevronRight,
} from "lucide-react";

const SEV_STYLE = {
  critical: { dot: "bg-status-critical", cls: "border-status-critical-border bg-status-critical-bg text-status-critical-text" },
  warning:  { dot: "bg-status-drifting", cls: "border-status-drifting-border bg-status-drifting-bg text-status-drifting-text" },
};

function DirIcon({ direction }) {
  if (direction === "up") return <TrendingUp className="w-3.5 h-3.5 text-status-onTrack" />;
  if (direction === "down") return <TrendingDown className="w-3.5 h-3.5 text-status-critical" />;
  return <Minus className="w-3.5 h-3.5 text-ink-400" />;
}

function Metric({ label, value, testid, highlight }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-wider text-ink-400">{label}</span>
      <span className={`text-[18px] font-bold leading-tight tabular-nums ${highlight ? "font-display text-blue-800" : "text-ink-600"}`}>{value}</span>
    </div>
  );
}

const VERDICT_STYLE = {
  on_track:  { label: "On Track",  cls: "border-status-onTrack-border bg-status-onTrack-bg text-status-onTrack-text" },
  at_risk:   { label: "At Risk",   cls: "border-status-drifting-border bg-status-drifting-bg text-status-drifting-text" },
  off_track: { label: "Off Track", cls: "border-status-critical-border bg-status-critical-bg text-status-critical-text" },
};

function PacingBar({ tp }) {
  const range = (tp.target_cr ?? 0) - (tp.baseline_cr ?? 0);
  const pct = (v) => (range > 0 && v != null) ? Math.min(100, Math.max(0, ((v - tp.baseline_cr) / range) * 100)) : 0;
  const currentPct = pct(tp.current_cr);
  const expectedPct = tp.expected_cr != null ? pct(tp.expected_cr) : null;
  const barCls = tp.verdict === "on_track" ? "bg-status-onTrack"
               : tp.verdict === "at_risk"  ? "bg-status-drifting"
               : "bg-status-critical";
  return (
    <div className="mt-4" data-testid="target-pacing-bar">
      <div className="relative h-3 rounded-full bg-ink-100 overflow-visible">
        <div className={`absolute inset-y-0 left-0 rounded-full ${barCls}`}
             style={{ width: `${currentPct}%` }} />
        {expectedPct != null && (
          <div className="absolute top-[-3px] h-[18px] w-[3px] rounded-full bg-ink-600"
               style={{ left: `calc(${expectedPct}% - 1.5px)` }} title="Where we should be by now" />
        )}
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-ink-400">
        <span>Baseline · ₹{tp.baseline_cr}Cr</span>
        <span>Target · ₹{tp.target_cr}Cr</span>
      </div>
    </div>
  );
}

function TargetPacingCard({ tp }) {
  if (!tp || !tp.available) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card text-center" data-testid="target-pacing-empty">
        <Target className="w-6 h-6 text-ink-300 mx-auto" />
        <p className="mt-2 text-[13px] text-ink-500">Set a strategic target in Company Trajectory to track pacing here.</p>
      </div>
    );
  }
  const v = VERDICT_STYLE[tp.verdict];
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card" data-testid="target-pacing-card">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold uppercase tracking-wider text-ink-500">Are we on track?</h3>
        {tp.pacing_available && v && (
          <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-full border ${v.cls}`}
                data-testid="target-pacing-status">
            {v.label}
            {tp.variance_pct != null && ` · ${tp.variance_pct > 0 ? "+" : ""}${tp.variance_pct}%`}
          </span>
        )}
      </div>
      <div className="mt-3 flex items-end gap-8">
        <Metric label="Baseline" value={tp.baseline_cr != null ? `₹${tp.baseline_cr}Cr` : "—"} testid="pacing-baseline" />
        <Metric label="Current" value={tp.current_cr != null ? `₹${tp.current_cr}Cr` : "—"} highlight testid="pacing-current" />
        {tp.pacing_available && <Metric label="Expected pace" value={`₹${tp.expected_cr}Cr`} testid="pacing-expected" />}
        <Metric label="Target" value={`₹${tp.target_cr}Cr`} testid="pacing-target" />
      </div>
      {tp.pacing_available ? <PacingBar tp={tp} /> : (
        <p className="mt-3 text-[12px] text-ink-400">Pacing needs a frozen baseline — will appear once available.</p>
      )}
    </div>
  );
}

export default function BusinessPerformancePanel({ analytical, systemBriefing }) {
  const navigate = useNavigate();
  if (!analytical) {
    return (
      <div className="rounded-xl border border-ink-200 bg-white p-8 text-center" data-testid="business-performance-empty">
        <p className="text-[13px] text-ink-500">No performance data yet.</p>
      </div>
    );
  }
  const { health_score, risk_level, kpi_trends = [], risks = [], interventions = [] } = analytical;

  return (
    <div className="space-y-5" data-testid="business-performance-panel">
      <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card">
        <div className="flex items-center gap-8">
          <Metric label="System Health" value={health_score != null ? `${health_score}/100` : "—"} testid="bp-health" />
          <Metric label="Risk" value={risk_level || "—"} testid="bp-risk" />
        </div>
        {systemBriefing && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-ink-50 border border-ink-100 p-3" data-testid="bp-briefing">
            <Sparkles className="w-4 h-4 text-blue-800 mt-0.5 shrink-0" />
            <p className="text-[13px] text-ink-700 leading-relaxed">{systemBriefing}</p>
          </div>
        )}
      </div>

      <TargetPacingCard tp={analytical.target_pacing} />

      {kpi_trends.length > 0 && (
        <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card" data-testid="kpi-trends-card">
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-ink-500 mb-3">KPI Trends</h3>
          <div className="flex flex-wrap gap-2">
            {kpi_trends.map((k, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-lg border border-ink-200 bg-ink-50 text-ink-700"
                    data-testid={`kpi-trend-${k.metric_id || i}`}>
                <DirIcon direction={k.direction} /> {k.label}
                {k.delta_pct != null && <span className="text-ink-400">({k.delta_pct > 0 ? "+" : ""}{k.delta_pct}%)</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card" data-testid="risks-card">
        <h3 className="text-[13px] font-semibold uppercase tracking-wider text-ink-500 mb-3">Risks</h3>
        {risks.length === 0 ? (
          <p className="text-[13px] text-ink-500">No active risks flagged right now.</p>
        ) : (
          <div className="space-y-2">
            {risks.map((r, i) => {
              const sv = SEV_STYLE[r.severity] || SEV_STYLE.warning;
              return (
                <div key={i} className={`flex items-start gap-2.5 rounded-lg border p-2.5 ${sv.cls}`} data-testid={`risk-${i}`}>
                  {r.kind === "conflict" ? <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                  <div>
                    <p className="text-[13px] font-semibold">{r.title}</p>
                    {r.detail && <p className="text-[12px] opacity-80 mt-0.5">{r.detail}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card" data-testid="interventions-card">
        <h3 className="text-[13px] font-semibold uppercase tracking-wider text-ink-500 mb-3">Current Interventions</h3>
        {interventions.length === 0 ? (
          <p className="text-[13px] text-ink-500">Nothing new to intervene on — see Execution for in-progress work.</p>
        ) : (
          <div className="space-y-2.5">
            {interventions.map((it, i) => (
              <div key={i} className="rounded-lg border border-ink-200 p-3" data-testid={`intervention-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-ink-100 text-ink-600 whitespace-nowrap shrink-0">
                    {it.source === "founder_created" && it.show_founder_badge === false
                      ? `Assigned to: ${it.owner_title}` : it.owner_title}
                  </span>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-ink-100 text-ink-500">P{it.priority}</span>
                  <span className="text-[14px] font-bold text-blue-800 ml-1">{it.title}</span>
                </div>
                <p className="mt-1.5 text-[12px] text-ink-800"><span className="font-semibold">Lever: </span>{it.action}</p>
                <button onClick={() => navigate(it.module_path)}
                        className="mt-1.5 text-[12px] font-medium text-blue-800 inline-flex items-center gap-1 hover:underline">
                  See in Execution <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
